<template>
  <MGuidedCheckoutStep
    step-number="2"
    title="Wie groß ist dein Projekt?"
    description="Gib deine Projektfläche an, damit wir die benötigte Paketmenge inklusive Verschnitt für dich berechnen."
  >
    <div class="product__buy-box m-gc-step-size__box">
      <div class="product__buy-box__inner">
        <div class="badge badge-gray">
          Menge wählen
        </div>

        <AProductQuantityCalculator
          v-if="mainOptionValue && mainOptionValue.product"
          :value="quantity"
          :product="mainOptionValue.product"
          :show-offcuts="true"
          unlimit-quantity
          @input="updateQuantity"
        />

        <div
          v-if="skirtMeters"
          class="m-gc-step-size__skirt-meters"
        >
          Geschätzte Leistenlänge: <strong>{{ skirtMetersLabel }}</strong>
        </div>
      </div>
    </div>
  </MGuidedCheckoutStep>
</template>

<script>
import MGuidedCheckoutStep from 'theme/components/molecules/guided-checkout/m-guided-checkout-step'
import AProductQuantityCalculator from 'theme/components/atoms/a-product-quantity-calculator'
import {
  estimateSkirtMeters,
  getMainBundleOption,
  setMainBundleQuantity
} from 'theme/helpers/guided-checkout'
import { getSelectedBundleOptionValue } from 'theme/helpers/bundle'

export default {
  name: 'MGcStepSize',
  components: {
    MGuidedCheckoutStep,
    AProductQuantityCalculator
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    quantity: {
      type: Number,
      default: 1
    },
    bundleOptionsComponent: {
      type: Object,
      default: null
    }
  },
  data () {
    const mainOption = getMainBundleOption(this.product)
    const mainOptionValue = mainOption
      ? getSelectedBundleOptionValue(this.product, mainOption)
      : null

    return {
      // Seeded with the real value so the counter only tweens on change, not on first paint.
      animatedMeters: estimateSkirtMeters(this.product, mainOptionValue) || 0,
      metersFrame: null
    }
  },
  computed: {
    mainOption () {
      return getMainBundleOption(this.product)
    },
    mainOptionValue () {
      return this.mainOption
        ? getSelectedBundleOptionValue(this.product, this.mainOption)
        : null
    },
    skirtMeters () {
      return estimateSkirtMeters(this.product, this.mainOptionValue) || 0
    },
    skirtMetersLabel () {
      return `${this.animatedMeters.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })} m`
    }
  },
  watch: {
    skirtMeters (target) {
      this.animateMeters(target)
    }
  },
  beforeDestroy () {
    if (this.metersFrame) {
      cancelAnimationFrame(this.metersFrame)
    }
  },
  methods: {
    updateQuantity (quantity) {
      this.$emit('input', quantity)
      this.$emit('changed', quantity)
      this.$nextTick(() => {
        setMainBundleQuantity(this.bundleOptionsComponent, this.product, quantity)
      })
    },
    animateMeters (target) {
      if (typeof requestAnimationFrame === 'undefined') {
        this.animatedMeters = target
        return
      }

      if (this.metersFrame) {
        cancelAnimationFrame(this.metersFrame)
        this.metersFrame = null
      }

      const from = this.animatedMeters
      const delta = target - from

      // Skip the tween for negligible changes (< 0.1 m) so it doesn't jitter.
      if (Math.abs(delta) < 0.1) {
        this.animatedMeters = target
        return
      }

      const duration = 450
      const startTime = (typeof performance !== 'undefined' ? performance.now() : Date.now())

      const tick = (now) => {
        const elapsed = (typeof performance !== 'undefined' ? now : Date.now()) - startTime
        const progress = Math.min(1, elapsed / duration)
        // easeOutCubic for a quick settle
        const eased = 1 - Math.pow(1 - progress, 3)
        this.animatedMeters = from + (delta * eased)

        if (progress < 1) {
          this.metersFrame = requestAnimationFrame(tick)
        } else {
          this.animatedMeters = target
          this.metersFrame = null
        }
      }

      this.metersFrame = requestAnimationFrame(tick)
    }
  }
}
</script>

<style lang="scss" scoped>
.m-gc-step-size {
  &__skirt-meters {
    margin-top: 0.75rem;
    line-height: 1.4;
    color: var(--c-dark-variant);
  }
}
</style>
